import { useCallback, useEffect, useRef, useState } from "react";
import supernovaChain, {
  SUPERNOVA_ERROR,
  toSupernovaWriteError,
} from "./supernovaChain";

/**
 * useSupernovaStake — the state machine behind the stake modal.
 *
 * It owns exactly two things: whether the staking contract is approved as an
 * operator on the member's collection, and where a member-signed transaction
 * currently is. It owns none of the form (type, quantity, lock) — that stays in
 * the modal, which is what makes a declined signature recoverable: the machine
 * resets, the member's choices don't.
 *
 * THE FOUR TRANSACTION STATES (supernovaChain.js documents the write contract
 * they map onto):
 *
 *   PENDING    the member is looking at their wallet's signature prompt.
 *              Nothing has been broadcast and nothing can be undone yet.
 *   SIGNED     they signed; a hash is on the wire, not yet mined.
 *   CONFIRMED  the transaction is mined. ONLY reachable when the service hands
 *              back a `confirmed` promise that actually resolves.
 *   REJECTED   they declined. A decision, not a failure — reset() puts the form
 *              back exactly as it was and nothing on-chain was touched.
 *
 * And two that are not transaction states at all:
 *
 *   UNAVAILABLE the service reports NOT_IMPLEMENTED — this build genuinely has
 *               no signing path (no verified ABI compiled in, or no browser
 *               wallet at all). The UI must label that plainly. NEVER a success.
 *   ERROR       something genuinely went wrong; member-safe copy, no stack.
 *
 * NON-CUSTODIAL. Nothing in this hook signs. Every write is a call into the
 * service, which prompts the member's own wallet. ARC Educational holds no key,
 * cannot sign for anyone, and cannot move an NFT.
 *
 * WHICH SERVICE FUNCTIONS THIS HOOK CALLS, AND WHY THEY ARE THE SEAMS:
 * `requestApprovalSignature` and `requestStakeSignature`, not `setApprovalForAll`
 * and `stake`. The seams are where the payload is built, logged and cross-checked
 * against the calldata by supernovaWriteGuards, and where the approval is skipped
 * if the wallet already granted it. Calling the raw writes from here would keep
 * the target-address guard but lose the payload cross-check — don't.
 *
 * NOTHING RETRIES BY ITSELF. Every recovery on this surface is a button the
 * member presses. A declined signature resets the machine and waits.
 *
 * NOT AN ENTITLEMENT. Approval and stake state come from the member's wallet
 * and the chain. Nothing here reads or writes a membership record.
 */

export const TX = {
  IDLE: "idle",
  PENDING: "pending",
  SIGNED: "signed",
  CONFIRMED: "confirmed",
  REJECTED: "rejected",
  UNAVAILABLE: "unavailable",
  ERROR: "error",
};

export const APPROVAL = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

/** States where a prompt is open or a transaction is in flight. */
export const isTxBusy = (state) => state === TX.PENDING || state === TX.SIGNED;

/** Terminal states the member has to acknowledge before anything else happens. */
export const isTxSettled = (state) =>
  state === TX.CONFIRMED ||
  state === TX.REJECTED ||
  state === TX.UNAVAILABLE ||
  state === TX.ERROR;

const PROMPT_MESSAGE =
  "Confirm the request in your wallet. Nothing is submitted until you sign.";
const SUBMITTED_MESSAGE =
  "Signed and submitted to Arbitrum. Waiting for it to be confirmed on-chain.";
/* The service resolved without a way to observe the mined transaction. We say
   what we actually know — "submitted" — instead of promoting it to confirmed. */
const NO_WATCH_MESSAGE =
  "Signed and submitted to Arbitrum. We can't follow it to confirmation from " +
  "here — check your wallet or a block explorer for the final result.";

const IDLE_TX = { state: TX.IDLE, message: null, hash: null, error: null };

const txFromError = (error) => {
  if (error && error.code === SUPERNOVA_ERROR.NOT_IMPLEMENTED) {
    return { state: TX.UNAVAILABLE, message: error.message, hash: null, error };
  }
  if (error && error.code === SUPERNOVA_ERROR.USER_REJECTED) {
    return { state: TX.REJECTED, message: error.message, hash: null, error };
  }
  return { state: TX.ERROR, message: error.message, hash: null, error };
};

/**
 * @param {string|null} wallet member's address
 * @param {Object} [options]
 * @param {boolean} [options.enabled] false ⇒ the hook does nothing at all: no
 *        read, no request, no network traffic. Callers pass the tab's flag in.
 * @param {Object} [options.service] injectable supernovaChain surface (tests)
 * @param {Object} [options.client]  injectable viem client (tests)
 */
export default function useSupernovaStake(wallet, options = {}) {
  const { enabled = false, service = supernovaChain, client } = options;
  const active = Boolean(enabled && wallet);

  const [approvalStatus, setApprovalStatus] = useState(APPROVAL.IDLE);
  const [approved, setApproved] = useState(null);
  const [approvalMessage, setApprovalMessage] = useState(null);

  const [approvalTx, setApprovalTx] = useState(IDLE_TX);
  const [stakeTx, setStakeTx] = useState(IDLE_TX);

  /* One counter for every async path in the hook. Bumping it invalidates
     anything still in flight, so a resolved call from a previous wallet (or
     from before an unmount) can never write over current state. */
  const runRef = useRef(0);
  useEffect(() => () => { runRef.current += 1; }, []);

  const refreshApproval = useCallback(async () => {
    if (!active) return;
    const runId = (runRef.current += 1);
    const current = () => runRef.current === runId;

    setApprovalStatus(APPROVAL.LOADING);
    setApprovalMessage(null);
    try {
      const result = await service.getApprovalStatus(wallet, { client });
      if (!current()) return;
      setApproved(Boolean(result?.approved));
      setApprovalStatus(APPROVAL.READY);
    } catch (e) {
      if (!current()) return;
      /* Unknown, NOT "not approved" — the modal must not send someone to sign
         an approval we never established they need. */
      setApproved(null);
      setApprovalMessage(
        e?.message || "Couldn't check whether you've approved the staking contract."
      );
      setApprovalStatus(APPROVAL.ERROR);
    }
  }, [active, wallet, service, client]);

  useEffect(() => {
    if (!active) {
      runRef.current += 1;
      setApprovalStatus(APPROVAL.IDLE);
      setApproved(null);
      setApprovalMessage(null);
      setApprovalTx(IDLE_TX);
      setStakeTx(IDLE_TX);
      return;
    }
    refreshApproval();
  }, [active, refreshApproval]);

  /**
   * Drive one member-signed write through PENDING → SIGNED → CONFIRMED, or off
   * to REJECTED / UNAVAILABLE / ERROR. Shared by approve() and submit() so both
   * transactions behave identically — including how a decline is handled.
   */
  const runWrite = useCallback(
    async (setTx, call, fallbackMessage) => {
      const runId = (runRef.current += 1);
      const current = () => runRef.current === runId;

      setTx({ state: TX.PENDING, message: PROMPT_MESSAGE, hash: null, error: null });

      let result;
      try {
        result = await call();
      } catch (raw) {
        const error = toSupernovaWriteError(raw, fallbackMessage);
        if (current()) setTx(txFromError(error));
        return { ok: false, error };
      }

      /* Nothing to sign after all: the wallet had already granted the operator
         approval, so the service short-circuited before opening a prompt. That
         is a success with no transaction — back to the form, no hash, no
         spinner, and certainly no "confirmed" for a tx that never existed. */
      if (result?.alreadyApproved) {
        if (current()) setTx(IDLE_TX);
        return { ok: true, alreadyApproved: true };
      }

      const hash = result?.hash || null;
      const canWatch = typeof result?.confirmed?.then === "function";

      if (!canWatch) {
        /* TODO(backend, supervised): a service that resolves without a
           `confirmed` promise leaves us at "submitted" for good. That is the
           honest end of the line — do not promote it to confirmed here. */
        if (current()) {
          setTx({ state: TX.SIGNED, message: NO_WATCH_MESSAGE, hash, error: null });
        }
        return { ok: false, signed: true, hash };
      }

      if (current()) {
        setTx({ state: TX.SIGNED, message: SUBMITTED_MESSAGE, hash, error: null });
      }

      try {
        await result.confirmed;
      } catch (raw) {
        const error = toSupernovaWriteError(raw, fallbackMessage);
        if (current()) setTx({ ...txFromError(error), hash });
        return { ok: false, error, hash };
      }

      if (current()) {
        setTx({ state: TX.CONFIRMED, message: null, hash, error: null });
      }
      return { ok: true, hash };
    },
    []
  );

  /**
   * The one-time ERC-1155 operator approval. Explained in plain language on
   * screen before it is ever offered — see SupernovaStakeModal.
   */
  const approve = useCallback(async () => {
    if (!active) return { ok: false };
    const outcome = await runWrite(
      setApprovalTx,
      () => service.requestApprovalSignature({ wallet }),
      "Couldn't approve the staking contract. Nothing changed — try again."
    );
    /* Re-read rather than assume: the chain is the source of truth for whether
       the approval actually landed. */
    if (outcome.ok) await refreshApproval();
    return outcome;
  }, [active, runWrite, service, wallet, refreshApproval]);

  /**
   * Lock NFTs. Refuses when we KNOW approval is missing — an unapproved stake
   * would prompt the member to sign a transaction that reverts. An approval
   * status we simply couldn't read does not block: the member's wallet gets the
   * final say, and we've told them we couldn't check.
   *
   * @param {{tokenId: number, qty: number, lockDays: number}} request
   */
  const submit = useCallback(
    async (request) => {
      if (!active) return { ok: false };
      if (approved === false) {
        const error = {
          code: SUPERNOVA_ERROR.WRITE_FAILED,
          message:
            "Approve the staking contract first — that one-time step lets it " +
            "move the NFTs you choose into the lock.",
        };
        setStakeTx({ state: TX.ERROR, message: error.message, hash: null, error });
        return { ok: false, error };
      }
      return runWrite(
        setStakeTx,
        () => service.requestStakeSignature({ wallet, ...request }),
        "Couldn't submit your stake. Nothing was locked — try again."
      );
    },
    [active, approved, runWrite, service, wallet]
  );

  /** Back to the form with the member's choices untouched. */
  const resetStakeTx = useCallback(() => setStakeTx(IDLE_TX), []);
  const resetApprovalTx = useCallback(() => setApprovalTx(IDLE_TX), []);
  const reset = useCallback(() => {
    runRef.current += 1;
    setStakeTx(IDLE_TX);
    setApprovalTx(IDLE_TX);
  }, []);

  return {
    approval: {
      status: approvalStatus,
      approved,
      message: approvalMessage,
      /* We know an approval is needed only when the read succeeded and said so.
         Unknown stays unknown. */
      needed: approvalStatus === APPROVAL.READY && approved === false,
      refresh: refreshApproval,
    },
    approvalTx,
    stakeTx,
    approve,
    submit,
    reset,
    resetStakeTx,
    resetApprovalTx,
    busy: isTxBusy(approvalTx.state) || isTxBusy(stakeTx.state),
  };
}
